import { Box, Stack, Typography } from '@mui/material';
import { FC, SyntheticEvent, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { useAppStore } from '../../app/api/app-api';
import AppSpinner from '../../common/components/AppSpinner';
import EmptyPageAvatar from '../../common/components/EmptyPageAvatar';
import { navigate, AppPages } from '../../common/routing/app-navigator';
import SidebarLayout from '../../elements/SidebarLayout/SidebarLayout';
import VerticalSplitPane from '../../elements/VerticalSplitPane/VerticalSplitPane';
import { useIsScanning } from '../../new-scan/actions/scan-actions';
import { loadSbomLibraries } from '../actions/sbom-libraries-actions';
import {
  loadFilters,
  setInitialFilters,
} from '../actions/sbom-libraries-filters-actions';
import SbomTable from '../components/SbomTable';
import SbomFiltersStore from '../stores/sbom-filters-store';
import SbomStore from '../stores/sbom-store';
import SbomControlContainer from './SbomControlContainer';
import SbomDrawerContainer from './SbomDrawerContainer';
import SbomSidebarContainer from './SbomSidebarContainer';

const SbomContainer: FC<React.PropsWithChildren<unknown>> = () => {
  const [searchParams] = useSearchParams();
  const filters = searchParams.get('filters');
  const { selectedSbom } = useSnapshot(SbomStore);
  useEffect(() => {
    setInitialFilters(filters);
    loadSbomLibraries({ update: true });
    loadFilters();
  }, [filters]);

  const handleClickConnectors = useCallback(
    (e: SyntheticEvent<HTMLElement>) => {
      e.preventDefault();
      navigate(AppPages.Connectors);
    },
    [],
  );
  const { loading, libraries } = useSnapshot(SbomStore);
  const { loadingFilters } = useSnapshot(SbomFiltersStore);
  const inProgress = useIsScanning();
  const { isEmptyOrg } = useAppStore();

  if (
    (loading.isIdle() || loading.isPending() || loadingFilters.isPending()) &&
    !libraries
  ) {
    return <AppSpinner text='Loading SBOM' />;
  }

  if (inProgress && libraries?.length === 0) {
    return (
      <EmptyPageAvatar
        text={`A scan is now running. The first results will appear soon so don't MOOOOve.`}
      />
    );
  }

  if (loading.isError()) {
    return (
      <Typography variant='body1' color='error'>
        Error loading SBOM list, please try again later
      </Typography>
    );
  }

  if (isEmptyOrg && !inProgress) {
    return (
      <EmptyPageAvatar
        text={
          <>
            Please run a scan to see SBOM list.
            <br />
            To run a scan...
            <Typography
              variant='body1'
              color='text.primary'
              component='a'
              onClick={handleClickConnectors}
              href={AppPages.Connectors}
              mt={1}
              style={{ display: 'block', fontWeight: 'bold', color: '#FFF' }}>
              Connect your Git projects
            </Typography>
          </>
        }
      />
    );
  }

  return (
    <SidebarLayout aside={<SbomSidebarContainer />}>
      <Stack direction='column' height='100%'>
        <SbomControlContainer />
        <Stack flex='1 1 auto' sx={{ overflowY: 'auto' }}>
          <Box width='100%' height='100%'>
            <VerticalSplitPane
              content={
                <Box width='100%' height='100%'>
                  {libraries && libraries.length !== 0 ? (
                    <SbomTable
                      selectedSbomId={selectedSbom?.id}
                      libraries={libraries}
                      loading={
                        loading.isPending() || loadingFilters.isPending()
                      }
                    />
                  ) : (
                    <Typography variant='body2' color='text.primary'>
                      No SBOM found
                    </Typography>
                  )}
                </Box>
              }>
              {({ className }) => <SbomDrawerContainer className={className} />}
            </VerticalSplitPane>
          </Box>
        </Stack>
      </Stack>
    </SidebarLayout>
  );
};

export default SbomContainer;
